import { ArrowLeft, LogIn, ShieldCheck, Sparkles } from "lucide-react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { env } from "../../lib/env";
import { supabase } from "../../lib/supabase/client";
import { useAuth } from "./AuthContext";
import { getOAuthRedirectUrl } from "./oauthRouting";

type LocationState = { returnTo?: string };

export function SignInPage() {
  const auth = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const callbackError = searchParams.get("error");
  const returnTo = (location.state as LocationState | null)?.returnTo ?? "/atlas";

  if (auth.user && auth.profile) {
    const destination = auth.profile.onboarding_complete
      ? auth.profile.status === "approved"
        ? returnTo
        : `/account/${auth.profile.status}`
      : "/onboarding";
    return <Navigate to={destination} replace />;
  }

  const signIn = async () => {
    if (!supabase) return;
    window.sessionStorage.setItem("guiding-stars:auth-return-to", returnTo);
    // OpenAI Sites serves client-side routes after the app has loaded, but a
    // direct request to /auth/callback is normalized to / by the host. Return
    // OAuth codes to the origin so the production host preserves the query;
    // main.tsx immediately rewrites that root callback to /auth/callback.
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: getOAuthRedirectUrl(env.VITE_SITE_URL),
      },
    });
  };

  return (
    <section className="auth-page">
      <div className="auth-stars" aria-hidden="true" />
      <div className="auth-card">
        <div className="auth-mark">
          <Sparkles aria-hidden="true" />
        </div>
        <p className="eyebrow">My Atlas</p>
        <h1>Welcome to your place in the sky.</h1>
        <p>
          Guiding Stars is The Guided Pathway Foundation’s internal service, learning, and
          recognition program. Sign in to view your private Foundation service record.
        </p>
        {(auth.error || callbackError) && (
          <p className="form-error" role="alert">
            {auth.error ??
              (callbackError === "session"
                ? "Google returned, but a secure session could not be completed. Please try again."
                : callbackError)}
          </p>
        )}
        {auth.configured ? (
          <button className="google-button" type="button" onClick={() => void signIn()}>
            <LogIn size={19} aria-hidden="true" /> Continue with Google
          </button>
        ) : (
          <div className="configuration-note" role="status">
            Add the public Supabase project values to enable Google sign-in.
          </div>
        )}
        <div className="auth-assurance">
          <ShieldCheck size={17} aria-hidden="true" />
          <span>Guiding Stars never stores your Google password.</span>
        </div>
        <Link className="auth-back" to="/">
          <ArrowLeft size={15} /> Return to the public site
        </Link>
      </div>
    </section>
  );
}
